import { useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { geocode, lerp, inServiceArea } from '../lib/geo';
import { isNative, startBackgroundTracking, stopBackgroundTracking } from '../lib/native';

const INTERVAL = 5000;

// Streams the driver position to the backend (real GPS, simulated fallback).
export const useDriverLocation = (driverId, order) => {
  const { sendDriverLocation } = useApp();
  const [position, setPosition] = useState(null);
  const [source, setSource] = useState(null);
  const lastSent = useRef(0);

  useEffect(() => {
    if (!driverId) return undefined;
    let watchId = null;
    let simTimer = null;
    let cancelled = false;
    let t = 0;

    const push = (lat, lng, src) => {
      if (cancelled) return;
      setPosition({ lat, lng });
      setSource(src);
      const ms = Date.now();
      if (ms - lastSent.current < INTERVAL - 500) return;
      lastSent.current = ms;
      sendDriverLocation(driverId, { lat, lng, source: src, orderId: order?.id || null });
    };

    const startSim = () => {
      if (simTimer || cancelled) return;
      const from = geocode(order?.pickup || order?.restaurant || 'Barrio El Centro, Tocoa');
      const to = geocode(order?.dropoff || order?.address || 'Barrio Las Flores, Tocoa');
      const tick = () => { t = t >= 1 ? 0 : Math.min(1, t + 0.06); const p = lerp(from, to, t); push(p.lat, p.lng, 'sim'); };
      tick();
      simTimer = setInterval(tick, INTERVAL);
    };

    if (isNative()) {
      // Native apps: a foreground service keeps sending the position with the screen off.
      startBackgroundTracking(({ lat, lng }) => {
        if (!inServiceArea({ lat, lng })) { startSim(); return; }
        push(lat, lng, 'gps');
      }).then((ok) => { if (!ok && !cancelled) startSim(); });
      return () => { cancelled = true; stopBackgroundTracking(); if (simTimer) clearInterval(simTimer); };
    }

    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          // Out-of-town device (demo from abroad) -> use the in-town simulated route
          if (!inServiceArea(p)) { startSim(); return; }
          push(p.lat, p.lng, 'gps');
        },
        () => startSim(),
        { enableHighAccuracy: true, maximumAge: 4000, timeout: 12000 },
      );
      // If the device never answers, fall back after 15s
      const guard = setTimeout(() => { if (!lastSent.current) startSim(); }, 15000);
      return () => { cancelled = true; clearTimeout(guard); if (watchId !== null) navigator.geolocation.clearWatch(watchId); if (simTimer) clearInterval(simTimer); };
    }
    startSim();
    return () => { cancelled = true; if (simTimer) clearInterval(simTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId, order?.id]);

  return { position, source };
};

export default useDriverLocation;
